import { Reveal } from "../lib/reveal";
import { Link } from "react-router-dom";
import { MapPin, ShieldCheck, FlaskConical } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";

const clients = [
  "Reliance Bio-Energy",
  "UltraTech Cement",
  "Dalmia Cement",
  "ACC Cement",
  "Hydro-safe Hosur",
];

const states = [
  { label: "Maharashtra", slug: "maharashtra" },
  { label: "Odisha", slug: "odisha" },
  { label: "Jharkhand", slug: "jharkhand" },
  { label: "West Bengal", slug: "west-bengal" },
  { label: "Assam", slug: "assam" },
  { label: "Eastern India", slug: null },
];

export const TrustReach = () => {
  const { t } = useLang();
  return (
    <section id="reach" data-testid="trust-reach" className="py-24 md:py-32 bg-bone">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Reveal className="flex items-center gap-3 mb-6">
              <span className="text-xs tracking-[0.22em] uppercase font-semibold text-moss">{t("Trusted on the ground", "मैदान पर विश्वसनीय")}</span>
            </Reveal>
            <Reveal>
              <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-[1.03] text-ink">
                {t("Two decades of research, from indigenous roots.", "दो दशकों का शोध, स्वदेशी जड़ों से।")}
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 text-base leading-relaxed text-slate max-w-md">
                {t(
                  "OED is a microbial diversity bio-solution derived from the dung and urine of indigenous Indian cows — native bacteria, fungi, natural fungicides and larvicides, refined over two decades into a modern, scalable platform.",
                  "OED देशी भारतीय गायों के गोबर व मूत्र से प्राप्त एक सूक्ष्मजीवी विविधता बायो-समाधान है — देशी बैक्टीरिया, कवक, प्राकृतिक कवकनाशी व लार्वानाशक, जिसे दो दशकों में एक आधुनिक, स्केलेबल मंच के रूप में परिष्कृत किया गया।"
                )}
              </p>
            </Reveal>

            <div className="mt-10 grid grid-cols-3 gap-6">
              {[
                { icon: FlaskConical, v: "4×10¹²", l: "microbes / ml" },
                { icon: MapPin, v: "6", l: "states across East & Central India" },
                { icon: ShieldCheck, v: "ISO", l: "14001:2015 certified" },
              ].map((s) => (
                <Reveal key={s.l}>
                  <s.icon size={20} strokeWidth={1.5} className="text-terracotta" />
                  <div className="font-serif text-3xl md:text-4xl text-ink mt-3 leading-none">{s.v}</div>
                  <div className="text-[0.7rem] tracking-[0.1em] uppercase text-stone mt-2">{s.l}</div>
                </Reveal>
              ))}
            </div>
          </div>

          <div className="lg:col-span-7">
            <Reveal>
              <div className="text-xs tracking-[0.16em] uppercase text-stone mb-5">{t("Deployed with", "इनके साथ तैनात")}</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-px bg-stone/20 border border-stone/20 rounded-3xl overflow-hidden">
                {clients.map((c) => (
                  <div key={c} className="bg-bone px-6 py-8 flex items-center justify-center text-center">
                    <span className="font-serif text-xl md:text-2xl text-ink/80">{c}</span>
                  </div>
                ))}
                <div className="bg-forest text-bone px-6 py-8 flex flex-col items-center justify-center text-center">
                  <span className="font-serif text-3xl">15</span>
                  <span className="text-[0.68rem] tracking-[0.12em] uppercase text-bone/70 mt-1">Municipal corporations</span>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="mt-6 flex flex-wrap gap-2">
                {states.map((s) =>
                  s.slug ? (
                    <Link
                      key={s.label}
                      to={`/regions/${s.slug}`}
                      data-testid={`region-chip-${s.slug}`}
                      className="text-xs tracking-[0.08em] uppercase text-moss border border-moss/30 rounded-full px-4 py-2 hover:bg-moss hover:text-bone transition-colors duration-300"
                    >
                      {s.label}
                    </Link>
                  ) : (
                    <span key={s.label} className="text-xs tracking-[0.08em] uppercase text-stone border border-stone/30 rounded-full px-4 py-2">
                      {s.label}
                    </span>
                  )
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
